// frontend/src/tenant/tenant-admin.ts
// テナント管理の土台（招待 / IP 許可リスト表示, 設計 §3/§5）。
// WCAG2: label 関連付け / caption 付きテーブル / role=alert / フォーカス可視。
// 認可はすべてサーバ側（tenant-admin のみ。M3 の authorize + handler 段再確認, R12）。SPA はフォームと表示のみ。
import { ROLE_VALUES, type Role } from "@cloud-checker/shared";
import { ApiError, type ApiClient } from "../api/client";

export interface TenantAdminDeps {
  readonly apiClient: ApiClient;
}

interface IpAllowlistResponse {
  readonly items: ReadonlyArray<{ readonly cidr: string }>;
}

const ROLE_LABELS: Record<Role, string> = {
  "tenant-admin": "テナント管理者",
  operator: "オペレーター",
  "cost-viewer": "コスト閲覧",
  "dashboard-viewer": "ダッシュボード閲覧",
};

export function renderTenantAdmin(root: HTMLElement, deps: TenantAdminDeps): void {
  root.replaceChildren();

  // ページ見出し（h1）は共通アプリシェルのヘッダが単一で保持する（二重 h1 を避ける）。
  const alert = document.createElement("div");
  alert.setAttribute("role", "alert");
  alert.setAttribute("aria-live", "assertive");
  alert.className = "min-h-[1.25rem] text-sm text-red-700";

  const reload = (): Promise<void> => loadIpAllowlist(deps, root, alert);

  root.append(alert, buildInviteSection(deps, alert), buildIpAllowlistSection(deps, alert, reload));

  void reload();
}

function buildInviteSection(deps: TenantAdminDeps, alert: HTMLElement): HTMLElement {
  const section = document.createElement("section");
  section.className = "mt-6";
  section.setAttribute("aria-labelledby", "invite-heading");

  const h2 = document.createElement("h2");
  h2.id = "invite-heading";
  h2.className = "text-lg font-medium text-slate-900";
  h2.textContent = "ユーザー招待（任意）";

  // 招待は任意（必須のオンボード手順ではない）。必要なときだけ実行する旨を明示する。
  const hint = document.createElement("p");
  hint.className = "mt-1 text-sm text-slate-500";
  hint.textContent = "メンバーの招待は任意です。必要なときに実行してください。";

  const form = document.createElement("form");
  form.noValidate = true;
  form.className = "mt-3 space-y-3";

  const emailLabel = document.createElement("label");
  emailLabel.htmlFor = "invite-email";
  emailLabel.className = "block text-sm font-medium text-slate-700";
  emailLabel.textContent = "招待先メールアドレス";

  const email = document.createElement("input");
  email.id = "invite-email";
  email.type = "email";
  email.required = true;
  email.autocomplete = "email";
  email.className =
    "mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-slate-900 focus:border-shinonome-700";

  const roleLabel = document.createElement("label");
  roleLabel.htmlFor = "invite-role";
  roleLabel.className = "block text-sm font-medium text-slate-700";
  roleLabel.textContent = "ロール";

  const role = document.createElement("select");
  role.id = "invite-role";
  role.className =
    "mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-slate-900 focus:border-shinonome-700";
  for (const value of ROLE_VALUES) {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = ROLE_LABELS[value];
    role.append(option);
  }

  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className =
    "rounded bg-shinonome-700 px-4 py-2 font-medium text-white hover:bg-shinonome-800 disabled:opacity-60";
  submit.textContent = "招待を送信";

  const status = document.createElement("p");
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  status.className = "text-sm text-slate-700";

  form.append(emailLabel, email, roleLabel, role, submit, status);
  section.append(h2, hint, form);

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    alert.textContent = "";
    status.textContent = "";
    submit.disabled = true;
    deps.apiClient
      .post("/user/invite", { email: email.value.trim(), role: role.value })
      .then(() => {
        status.textContent = "招待を送信しました。";
        email.value = "";
      })
      .catch((err: unknown) => {
        alert.textContent = inviteErrorMessage(err);
      })
      .finally(() => {
        submit.disabled = false;
      });
  });

  return section;
}

function buildIpAllowlistSection(
  deps: TenantAdminDeps,
  alert: HTMLElement,
  reload: () => Promise<void>,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "mt-8";
  section.setAttribute("aria-labelledby", "ip-heading");

  const h2 = document.createElement("h2");
  h2.id = "ip-heading";
  h2.className = "text-lg font-medium text-slate-900";
  h2.textContent = "IP 許可リスト";

  const hint = document.createElement("p");
  hint.className = "mt-1 text-sm text-slate-500";
  hint.textContent = "ここで許可した IP（CIDR）からのみ API アクセスを認めます。自分のアクセス元 IP を必ず含めてください。";

  section.append(h2, hint, buildIpAddForm(deps, alert, reload));

  const table = document.createElement("table");
  table.id = "ip-table";
  table.className = "mt-3 w-full border-collapse text-sm";

  const caption = document.createElement("caption");
  caption.className = "sr-only";
  caption.textContent = "このテナントで許可されている IP（CIDR）の一覧";

  const thead = document.createElement("thead");
  const headRow = document.createElement("tr");
  const th = document.createElement("th");
  th.scope = "col";
  th.className = "border-b border-slate-200 py-2 text-left font-medium text-slate-700";
  th.textContent = "CIDR";
  headRow.append(th);
  thead.append(headRow);

  const tbody = document.createElement("tbody");
  tbody.id = "ip-tbody";

  table.append(caption, thead, tbody);
  section.append(table);
  return section;
}

// 簡易 CIDR 形式チェック（IPv4/IPv6 + プレフィックス）。最終検証はサーバ側（CIDR_PATTERN）。
function looksLikeCidr(value: string): boolean {
  return /^[0-9a-fA-F:.]+\/\d{1,3}$/.test(value);
}

// IP 許可リストへの追加フォーム（POST /tenant/ip-allowlist）。成功で一覧を再読込する。
function buildIpAddForm(
  deps: TenantAdminDeps,
  alert: HTMLElement,
  reload: () => Promise<void>,
): HTMLElement {
  const form = document.createElement("form");
  form.noValidate = true;
  form.className = "mt-3 flex flex-wrap items-end gap-3";

  const cidrWrap = document.createElement("div");
  const cidrLabel = document.createElement("label");
  cidrLabel.htmlFor = "ip-cidr";
  cidrLabel.className = "block text-sm font-medium text-slate-700";
  cidrLabel.textContent = "CIDR（例: 203.0.113.5/32）";
  const cidr = document.createElement("input");
  cidr.id = "ip-cidr";
  cidr.type = "text";
  cidr.required = true;
  cidr.placeholder = "203.0.113.5/32";
  cidr.className =
    "mt-1 block w-56 rounded border border-slate-300 px-3 py-2 font-mono text-slate-900 focus:border-shinonome-700";
  cidrWrap.append(cidrLabel, cidr);

  const labelWrap = document.createElement("div");
  const labelLabel = document.createElement("label");
  labelLabel.htmlFor = "ip-label";
  labelLabel.className = "block text-sm font-medium text-slate-700";
  labelLabel.textContent = "ラベル（任意）";
  const label = document.createElement("input");
  label.id = "ip-label";
  label.type = "text";
  label.placeholder = "本社オフィス など";
  label.className =
    "mt-1 block w-56 rounded border border-slate-300 px-3 py-2 text-slate-900 focus:border-shinonome-700";
  labelWrap.append(labelLabel, label);

  const submit = document.createElement("button");
  submit.type = "submit";
  submit.dataset["testid"] = "ip-add-submit";
  submit.className =
    "rounded bg-shinonome-700 px-4 py-2 font-medium text-white hover:bg-shinonome-800 disabled:opacity-60";
  submit.textContent = "IP を追加";

  const status = document.createElement("p");
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  status.className = "w-full text-sm text-slate-700";

  form.append(cidrWrap, labelWrap, submit, status);

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    alert.textContent = "";
    status.textContent = "";
    const cidrVal = cidr.value.trim();
    if (!looksLikeCidr(cidrVal)) {
      alert.textContent = "CIDR の形式が正しくありません（例: 203.0.113.5/32）。";
      return;
    }
    submit.disabled = true;
    deps.apiClient
      .post("/tenant/ip-allowlist", { cidr: cidrVal, label: label.value.trim() })
      .then(() => {
        status.textContent = "IP を追加しました。";
        cidr.value = "";
        label.value = "";
        return reload();
      })
      .catch((err: unknown) => {
        alert.textContent = inviteErrorMessage(err);
      })
      .finally(() => {
        submit.disabled = false;
      });
  });

  return form;
}

async function loadIpAllowlist(
  deps: TenantAdminDeps,
  root: HTMLElement,
  alert: HTMLElement,
): Promise<void> {
  const tbody = root.querySelector<HTMLElement>("#ip-tbody");
  if (tbody === null) return;
  try {
    const res = await deps.apiClient.get<IpAllowlistResponse>("/tenant/ip-allowlist");
    tbody.replaceChildren();
    if (res.items.length === 0) {
      const row = document.createElement("tr");
      const cell = document.createElement("td");
      cell.className = "py-2 text-slate-500";
      cell.textContent = "許可 IP は登録されていません。";
      row.append(cell);
      tbody.append(row);
      return;
    }
    for (const item of res.items) {
      const row = document.createElement("tr");
      row.dataset["testid"] = "ip-row";
      const cell = document.createElement("td");
      cell.className = "border-b border-slate-100 py-2 font-mono text-slate-800";
      cell.textContent = item.cidr;
      row.append(cell);
      tbody.append(row);
    }
  } catch (err: unknown) {
    alert.textContent = inviteErrorMessage(err);
  }
}

function inviteErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return "この操作を行う権限がありません。";
    if (err.status === 401) return "セッションが切れています。再度ログインしてください。";
  }
  return "処理に失敗しました。時間をおいて再試行してください。";
}
